import api from '../../services/api'

export default {
  namespaced: true,

  state: () => ({
    jobs: [],
    phases: [],
    selectedJob: null,
    selectedPhase: null,
    loadingJobs: false,
    loadingPhases: false,
    error: null,
    // Cache with TTL
    jobsCachedAt: null,
    phasesCachedAt: null,
    cacheTTL: 5 * 60 * 1000, // 5 minutes
    lastSort: 'latest'
  }),

  mutations: {
    SET_JOBS(state, jobs) {
      state.jobs = jobs
      state.jobsCachedAt = Date.now()
    },
    SET_PHASES(state, phases) {
      state.phases = phases
      state.phasesCachedAt = Date.now()
    },
    SET_SELECTED_JOB(state, job) {
      state.selectedJob = job
      // Clear phases when job changes
      state.phases = []
      state.selectedPhase = null
      state.phasesCachedAt = null
    },
    SET_SELECTED_PHASE(state, phase) {
      state.selectedPhase = phase
    },
    SET_LOADING_JOBS(state, loading) {
      state.loadingJobs = loading
    },
    SET_LOADING_PHASES(state, loading) {
      state.loadingPhases = loading
    },
    SET_ERROR(state, error) {
      state.error = error
    },
    CLEAR_SELECTION(state) {
      state.selectedJob = null
      state.selectedPhase = null
      state.phases = []
      state.phasesCachedAt = null
    },
    CLEAR_CACHE(state) {
      state.jobs = []
      state.phases = []
      state.jobsCachedAt = null
      state.phasesCachedAt = null
    }
  },

  actions: {
    /**
     * Fetch jobs from Synergist API
     * Uses cache if available and not expired
     * @param {Object} options - { force, search, sort, clientCode }
     *   sort: 'latest' (default), 'jobNumber_asc', 'jobNumber_desc'
     *   clientCode: filter by Synergist client code (e.g., "1/C0001")
     */
    async fetchJobs({ commit, state }, { force = false, search = '', sort = 'latest', clientCode = '' } = {}) {
      // Check cache unless forced refresh or different params
      const cacheKey = `${sort}-${clientCode}`
      if (!force && state.jobsCachedAt && !search && state.lastSort === cacheKey) {
        const cacheAge = Date.now() - state.jobsCachedAt
        if (cacheAge < state.cacheTTL && state.jobs.length > 0) {
          return state.jobs
        }
      }

      commit('SET_LOADING_JOBS', true)
      commit('SET_ERROR', null)

      try {
        const params = { sort }
        if (search) params.search = search
        if (clientCode) params.clientCode = clientCode

        const response = await api.get('/synergist/jobs', { params })
        const jobs = response.data.data.jobs || []

        // Only cache if no search filter
        if (!search) {
          commit('SET_JOBS', jobs)
          state.lastSort = cacheKey
        }

        return jobs
      } catch (error) {
        const message = error.response?.data?.error || 'Failed to fetch jobs from Synergist'
        commit('SET_ERROR', message)
        throw error
      } finally {
        commit('SET_LOADING_JOBS', false)
      }
    },

    /**
     * Fetch phases for a specific job
     */
    async fetchPhasesForJob({ commit, state }, jobCode) {
      if (!jobCode) {
        commit('SET_PHASES', [])
        return []
      }

      commit('SET_LOADING_PHASES', true)
      commit('SET_ERROR', null)

      try {
        // URL-encode jobCode since it contains "/" (e.g., "1/HOLS")
        const response = await api.get(`/synergist/jobs/${encodeURIComponent(jobCode)}/phases`)
        const phases = response.data.data.phases || []
        commit('SET_PHASES', phases)
        return phases
      } catch (error) {
        const message = error.response?.data?.error || 'Failed to fetch phases from Synergist'
        commit('SET_ERROR', message)
        throw error
      } finally {
        commit('SET_LOADING_PHASES', false)
      }
    },

    /**
     * Select a job and fetch its phases
     */
    async selectJob({ commit, dispatch }, job) {
      commit('SET_SELECTED_JOB', job)
      if (job) {
        await dispatch('fetchPhasesForJob', job.jobNumber)
      }
    },

    /**
     * Select a phase
     */
    selectPhase({ commit }, phase) {
      commit('SET_SELECTED_PHASE', phase)
    },

    /**
     * Clear all selections
     */
    clearSelection({ commit }) {
      commit('CLEAR_SELECTION')
    },

    /**
     * Test Synergist connection
     */
    async testConnection({ commit }) {
      commit('SET_ERROR', null)
      try {
        const response = await api.get('/synergist/test')
        return response.data
      } catch (error) {
        const message = error.response?.data?.error || 'Failed to connect to Synergist'
        commit('SET_ERROR', message)
        throw error
      }
    }
  },

  getters: {
    allJobs: (state) => state.jobs,
    phasesForJob: (state) => state.phases,
    selectedJob: (state) => state.selectedJob,
    selectedPhase: (state) => state.selectedPhase,
    loadingJobs: (state) => state.loadingJobs,
    loadingPhases: (state) => state.loadingPhases,
    error: (state) => state.error,

    // Check if both job and phase are selected
    hasValidSelection: (state) => {
      return state.selectedJob && state.selectedPhase
    },

    // Get the synergistPhaseId for stage creation
    synergistPhaseId: (state) => {
      return state.selectedPhase?.phaseJobAndPhase || null
    }
  }
}
